import { merge } from '../../../utils'
import { removeEmptyItems } from '../../../../../common/util'
import { type FreeActivityRecord } from '../../../../../models/FreeActivityRecord'

export type { FreeActivityRecord }
export type BlockingSelection = Record<string, string[]>
export type EntityOption = { id: string; label: string }

const isPrimitiveId = (value: unknown): value is string | number => (typeof value === 'string' || typeof value === 'number') && !Number.isNaN(value as number)

export const parseFreeActivities = (value: unknown): FreeActivityRecord[] => {
  if (!Array.isArray(value)) {
    return []
  }

  const order: string[] = []
  const collapsed = new Map<string, 'all' | string[]>()

  for (const entry of value) {
    if (entry === null || typeof entry !== 'object' || Array.isArray(entry)) {
      continue
    }
    const record = entry as Record<string, unknown>

    if (!isPrimitiveId(record.activityCodeId)) {
      continue
    }
    const activityCodeId = String(record.activityCodeId).trim()
    if (activityCodeId === '') {
      continue
    }

    const scope = record.scope
    if (scope !== 'all' && scope !== 'categories') {
      continue
    }

    if (scope === 'categories') {
      if (!Array.isArray(record.categoryIds)) {
        continue
      }
      const categoryIds: string[] = []
      for (const raw of record.categoryIds) {
        if (!isPrimitiveId(raw)) {
          continue
        }
        const id = String(raw).trim()
        if (id !== '' && !categoryIds.includes(id)) {
          categoryIds.push(id)
        }
      }
      if (categoryIds.length === 0) {
        continue
      }

      if (!collapsed.has(activityCodeId)) {
        order.push(activityCodeId)
      }
      const existing = collapsed.get(activityCodeId)
      if (existing === 'all') {
        continue
      }
      const union = existing ? [...existing] : []
      for (const id of categoryIds) {
        if (!union.includes(id)) {
          union.push(id)
        }
      }
      collapsed.set(activityCodeId, union)
    } else {
      if (!collapsed.has(activityCodeId)) {
        order.push(activityCodeId)
      }
      collapsed.set(activityCodeId, 'all')
    }
  }

  return order.map((activityCodeId) => {
    const collapsedValue = collapsed.get(activityCodeId)!
    return collapsedValue === 'all' ? { activityCodeId, scope: 'all' } : { activityCodeId, scope: 'categories', categoryIds: collapsedValue }
  })
}

export const recordsToBlockingSelection = (records: FreeActivityRecord[], knownActivityCodeIds: string[], knownCategoryIds: string[]): BlockingSelection => {
  const recordByCode = new Map(records.map((record) => [record.activityCodeId, record]))
  const selection: BlockingSelection = {}

  for (const activityCodeId of knownActivityCodeIds) {
    const record = recordByCode.get(activityCodeId)
    if (!record) {
      selection[activityCodeId] = [...knownCategoryIds]
    } else if (record.scope === 'all') {
      selection[activityCodeId] = []
    } else {
      const free = new Set(record.categoryIds)
      selection[activityCodeId] = knownCategoryIds.filter((id) => !free.has(id))
    }
  }

  return selection
}

export const blockingSelectionToRecords = (selection: BlockingSelection, knownCategoryIds: string[]): FreeActivityRecord[] => {
  const records: FreeActivityRecord[] = []

  for (const activityCodeId of Object.keys(selection)) {
    const blocking = new Set(selection[activityCodeId] ?? [])
    const free = knownCategoryIds.filter((id) => !blocking.has(id))
    if (free.length === 0) {
      continue
    }
    if (free.length === knownCategoryIds.length) {
      records.push({ activityCodeId, scope: 'all' })
    } else {
      records.push({ activityCodeId, scope: 'categories', categoryIds: free })
    }
  }

  return records
}

export const deriveSubmitRecords = ({
  listsTrusted,
  hideActivities,
  selection,
  rawFreeActivities,
  knownCategoryIds,
}: {
  listsTrusted: boolean
  hideActivities: boolean
  selection: BlockingSelection
  rawFreeActivities: unknown
  knownCategoryIds: string[]
}): FreeActivityRecord[] | null => {
  if (!listsTrusted) {
    return Array.isArray(rawFreeActivities) ? (rawFreeActivities as FreeActivityRecord[]) : null
  }
  return hideActivities ? blockingSelectionToRecords(selection, knownCategoryIds) : []
}

export const buildCalendarServiceConfig = (
  storedServiceConfig: { [key: string]: unknown } | undefined,
  enabled: boolean,
  freeActivities: FreeActivityRecord[] | null,
): { [key: string]: unknown } => {
  const calendar = freeActivities === null ? { enabled } : { enabled, freeActivities }
  const serviceConfig = merge(storedServiceConfig || {}, { inbound: { calendar } })
  return removeEmptyItems(serviceConfig) as { [key: string]: unknown }
}

export type SelectionBadgeStatus = 'excluded' | 'included' | 'partial'

export const selectionBadgeStatus = (selectedCount: number, totalCount: number): SelectionBadgeStatus => {
  if (selectedCount === 0) {
    return 'excluded'
  }
  return selectedCount === totalCount ? 'included' : 'partial'
}

export const subtitleForSelection = (selectedIds: string[], categoryOptions: EntityOption[]): string | null => {
  if (selectedIds.length === 0) {
    return null
  }
  if (selectedIds.length === categoryOptions.length) {
    return 'All categories'
  }
  const labelById = new Map(categoryOptions.map((option) => [option.id, option.label]))
  return selectedIds.map((id) => labelById.get(id) ?? id).join(', ')
}
